import t, { Intl } from '@jetshop/intl';
import React, { useState } from 'react';
import { styled } from 'linaria/react';
import { Formik, Form } from 'formik';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import InputWithLabel, { TextareaWithLabel } from '../ui/Input';
import Button from '../ui/Button';
import { useLocation } from 'react-router';

const Container = styled('div')`
  margin: 16px 0 24px;

  h2 {
    color: #000;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 160%; /* 22.4px */
    margin: 0;
  }

  p {
    color: #000;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%;
  }

  form {
    position: relative;
    margin-top: 16px;
    label {
      display: none;
    }
    .input-wrapper,
    .textarea-wrapper {
      margin-bottom: 0;
    }
    input,
    textarea {
      border: 1px solid #d2d2d2;
      border-radius: 0;
    }
  }
`;

const SubmitContainer = styled('div')`
  &.centerSubmit {
    text-align: center;
  }
`;

const RespondContainer = styled('div')`
  position: absolute;
  top: 100%;
  p {
    text-align: left;
    font-size: 12px;
    margin-top: 10px;
    color: #111;
    &.error {
      color: #ff0000;
    }
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  width: 100%;
  height: 40px;
  padding: 8px 125px;
  margin-top: 12px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 50px;
  text-transform: uppercase;
`;

// Component that uses reCAPTCHA hook
const FormWithRecaptcha = ({
  onSubmit,
  initialValues,
  validation,
  children,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (values, formikHelpers) => {
    if (!executeRecaptcha) {
      console.error('reCAPTCHA not loaded');
      return;
    }

    try {
      const token = await executeRecaptcha('submit_contact_form');
      await onSubmit({ ...values, recaptchaToken: token }, formikHelpers);
    } catch (error) {
      console.error('reCAPTCHA error:', error);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={validation}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  );
};

export const ProductContactForm = ({
  centerSubmit = false,
  hideTarget,
  isModal,
  productName,
  email,
}) => {
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const location = useLocation();
  const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  function validation(values) {
    const errors = {};
    Object.keys(values).forEach((key) => {
      if (key !== 'recaptchaToken' && !values[key]) errors[key] = 'Required';
    });
    return errors;
  }

  function onSubmit(values, { setSubmitting }) {
    setIsError(false);
    setIsSuccess(false);

    const apiUrl = '/api/submit-contact-form';

    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ ...values, emailTo: email }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(({ respond, error }) => {
        if (error && error.length > 0) {
          setIsError(true);
        } else if (!respond || respond.length === 0) {
          setIsError(true);
        } else {
          setIsSuccess(true);
        }
      })
      .catch((error) => {
        console.error('Form submission error:', error);
        setIsError(true);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  const initialValues = {
    name: '',
    email: '',
    message: '',
    subject: `Frågor om ${productName}`,
    url: location?.pathname,
    recaptchaToken: '',
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
      <FormWithRecaptcha
        onSubmit={onSubmit}
        initialValues={initialValues}
        validation={validation}
      >
        <ContactFormInner
          isError={isError}
          isSuccess={isSuccess}
          centerSubmit={centerSubmit}
          email={email}
          hideTarget={hideTarget}
          isModal={isModal}
          subjectField
          productName={productName}
          location={location}
        />
      </FormWithRecaptcha>
    </GoogleReCaptchaProvider>
  );
};

const ContactForm = ({ centerSubmit = false, store, hideTarget, isModal }) => {
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  const email =
    store?.contact?.match(/"mailto:([^"]+)"/)?.[1] ||
    'kundtjanst@mobelmastarna.se';

  function validation(values) {
    const errors = {};
    Object.keys(values).forEach((key) => {
      if (key !== 'recaptchaToken' && !values[key]) errors[key] = 'Required';
    });
    return errors;
  }

  function onSubmit(values, { setSubmitting }) {
    setIsError(false);
    setIsSuccess(false);

    const apiUrl = '/api/submit-contact-form';

    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ ...values, emailTo: email }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(({ respond, error }) => {
        if (error && error.length > 0) {
          setIsError(true);
        } else if (!respond || respond.length === 0) {
          setIsError(true);
        } else {
          setIsSuccess(true);
        }
      })
      .catch((error) => {
        console.error('Form submission error:', error);
        setIsError(true);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  const initialValues = {
    name: '',
    email: '',
    message: '',
    recaptchaToken: '',
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
      <FormWithRecaptcha
        onSubmit={onSubmit}
        initialValues={initialValues}
        validation={validation}
      >
        <ContactFormInner
          isError={isError}
          isSuccess={isSuccess}
          centerSubmit={centerSubmit}
          email={email}
          hideTarget={hideTarget}
          isModal={isModal}
        />
      </FormWithRecaptcha>
    </GoogleReCaptchaProvider>
  );
};

const ContactFormInner = ({
  isError,
  isSuccess,
  centerSubmit,
  email,
  hideTarget,
  isModal,
  productName = '[produkt saknas]',
  subjectField = false,
  location,
}) => (
  <Container>
    <h2>{t('Contact form')}</h2>
    {!isSuccess && (
      <>
        <p>{t("Please fill out the form and we'll assist you.")}</p>
        <Intl>
          {(t) => (
            <Form>
              <InputWithLabel
                type="text"
                name="name"
                label={t('Name')}
                placeholder={t('Name')}
                required
              />

              <input
                className="url"
                type="hidden"
                name="url"
                label="Url"
                required
                disabled
              />

              <input
                className="subject"
                type="hidden"
                name="subject"
                label="Ämne"
                required
                disabled
              />

              <InputWithLabel
                type="email"
                name="email"
                label={t('Email')}
                placeholder={t('E-mail')}
                required
              />
              <TextareaWithLabel
                type="text"
                name="message"
                label={t('Message')}
                placeholder={t('Message')}
                required
              />
              <SubmitContainer
                centerSubmit={centerSubmit}
                className={centerSubmit && 'centerSubmit'}
              >
                <StyledButton type="submit">{t('Send')}</StyledButton>
              </SubmitContainer>
              {(isError || isSuccess) &&
                (console.error('error', isError),
                (
                  <RespondContainer>
                    {isError && (
                      <p className="error">
                        {t(
                          'There was an error with sending your message. Please contact us on {email}.',
                          { email }
                        )}
                      </p>
                    )}
                  </RespondContainer>
                ))}
            </Form>
          )}
        </Intl>
      </>
    )}
    {isSuccess && (
      <>
        <p className="success" style={{ marginBottom: '1rem' }}>
          {t('Your message has been sent. We will get back to you shortly.')}
        </p>
        {isModal && (
          <StyledButton onClick={hideTarget} type="button">
            {t('Close')}
          </StyledButton>
        )}
      </>
    )}
  </Container>
);

export default ContactForm;
